import { jsx as _jsx } from "react/jsx-runtime";
import Box from "@mui/material/Box";
import { useMediaQuery } from "@mui/system";
import { useShowDebugAttributes } from "@plansoft-configuration-renderer/debug";
import { LayoutCell } from "./LayoutCell";
import { useLayoutGridSettingsContext } from "./LayoutGridSettingsContext";
import { LayoutGridType } from "./LayoutGridType";
import { PluginHelper } from "./helpers/PluginHelper";
import { useLayoutGridContainerComputedProps } from "./useLayoutGridContainerComputedProps";
import { useLayoutGridItemComputedProps } from "./useLayoutGridItemComputedProps";
/**
 * Renders layout grid (layout rows, columns)
 * @param props
 * @returns
 */
export function LayoutCssGrid(props) {
    const { node, gridType } = props;
    const { rowSpacing, columnSpacing, columns } = useLayoutGridContainerComputedProps(props);
    const getItemComputedProps = useLayoutGridItemComputedProps();
    const { cells } = node;
    const showAttributes = useShowDebugAttributes();
    const { flexLayoutBreakpoint } = useLayoutGridSettingsContext();
    const matchesFlexLayoutBreakpoint = useMediaQuery((theme) => theme.breakpoints.down(flexLayoutBreakpoint));
    const flexStyles = {
        display: "flex",
        flexDirection: "column",
        flexWrap: "wrap",
    };
    return (_jsx(Box, { className: `LayoutGrid-${node.id} gridType-${gridType}`, sx: matchesFlexLayoutBreakpoint
            ? flexStyles
            : getLayoutGridTypeStyles(gridType, cells, rowSpacing, columnSpacing, columns), children: cells.map((cell) => {
            var _a, _b;
            const rowsLength = (_b = (_a = cell.rows) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 1;
            // we currently ignore the sizes property, as this requires use of media queries to determine the right breakpoint
            const { sx: sxPropsFromLayout, className, size: cellSize, sizes: cellSizes, } = getItemComputedProps({
                cell,
            });
            const sxPropsComputed = matchesFlexLayoutBreakpoint
                ? flexStyles
                : getGridItemStyles({
                    pluginId: PluginHelper.getPluginId(cell),
                    cellSize,
                    cellSizes,
                    rowsLength: showAttributes ? rowsLength + 1 : rowsLength,
                });
            return (_jsx(Box, { className: className, sx: Object.assign(Object.assign({}, sxPropsFromLayout), sxPropsComputed), children: _jsx(LayoutCell, { node: cell }) }, cell.id));
        }) }));
}
function getLayoutGridTypeStyles(type, cells, rowGap, columnGap, totalColumns) {
    const commonStyles = {
        display: "grid",
        columnGap: `${columnGap / 2}rem`,
        rowGap: `${rowGap / 4}rem`,
        gridAutoRows: "min-content",
    };
    const styles = {
        [LayoutGridType.Container]: Object.assign(Object.assign({}, commonStyles), { gridTemplateColumns: `repeat(${totalColumns}, 1fr)` }),
        [LayoutGridType.Attribute]: Object.assign(Object.assign({}, commonStyles), { gridTemplateColumns: "subgrid", gridTemplateRows: "subgrid", gridColumn: "1 / -1" }),
        [LayoutGridType.Control]: Object.assign(Object.assign({}, commonStyles), { alignItems: "center", minWidth: 0, width: "100%", display: "flex" }),
        [LayoutGridType.Layout]: Object.assign({}, commonStyles),
        [LayoutGridType.Value]: Object.assign(Object.assign({}, commonStyles), { 
            //we need % proportions here, to prevent overflow of columns with gap
            gridTemplateColumns: cells
                .map((cell) => {
                var _a;
                const cellProportion = ((_a = cell.size) !== null && _a !== void 0 ? _a : 1) / totalColumns;
                return `${cellProportion * 100}%`;
            })
                .join(" ") }),
    };
    return styles[type];
}
function getGridItemStyles({ pluginId, cellSize, cellSizes, rowsLength, }) {
    const commonStyles = {
        gridColumn: getGridColumn(cellSize, cellSizes),
        gridRow: `span ${rowsLength}`,
    };
    if (pluginId.includes("AttributeComponent:AttributeHeadline")) {
        return {
            alignSelf: "flex-end",
            gridColumn: "inherit",
            minWidth: 0, //prevents overflow of debug pill
        };
    }
    else if (pluginId.includes("Attribute:")) {
        return Object.assign(Object.assign({}, commonStyles), { display: "grid", gridTemplateColumns: "subgrid", gridTemplateRows: "subgrid" });
    }
    else if (pluginId.includes("ConfigurationComponent:ConfigurationStatus") ||
        pluginId.includes("Control:substructure")) {
        return {
            display: "block",
            gridColumn: "-1 / 1",
        };
    }
    else if (pluginId.includes("ValueContent:")) {
        return {
            gridRow: `span ${rowsLength}`,
        };
    }
    else {
        return Object.assign(Object.assign({}, commonStyles), { gridTemplateColumns: "", gridTemplateRows: "" });
    }
}
function getGridColumn(size = 1, sizes) {
    if (sizes == null) {
        return gridColumnSpan(size);
    }
    const { xl, lg, md, sm, xs } = sizes;
    return {
        xs: gridColumnSpan(xs),
        sm: gridColumnSpan(sm),
        md: gridColumnSpan(md),
        lg: gridColumnSpan(lg),
        xl: gridColumnSpan(xl),
    };
}
function gridColumnSpan(size) {
    return size ? `span ${size}` : undefined;
}
//# sourceMappingURL=LayoutCssGrid.js.map